import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Section, Loading } from "../components/Common";
import { Save, Palette } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["primary_color", "Primary Colour (hex)", "text"],
  ["accent_color", "Accent Colour (hex)", "text"],
  ["logo_url", "Logo URL", "url"],
  ["motto", "Motto", "text"],
  ["cover_image_url", "Cover Image URL", "url"],
  ["watermark_text", "Watermark", "text"],
  ["seal_text", "Seal Text", "text"],
  ["header_note", "Header Note", "text"],
  ["footer_note", "Footer Note", "text"],
  ["signature_name", "Default Signature Name", "text"],
];

export default function DocumentDesigner() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.designer().then(({ data }) => {
    setData(data);
    setForm(data.branding || {});
  });

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.updateDesigner(form);
      toast.success("Branding saved — will apply to next generated document");
      load();
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };

  if (!data) return <Loading />;

  return (
    <div data-testid="designer-page">
      <PageHeader
        kicker="Professional Document Designer"
        title="School Branding & Publishing Configuration"
        subtitle="Configure how every Education360 document appears for this school — logo, palette, seal, watermark, header/footer, signature. No code changes required."
      />

      <div className="grid md:grid-cols-3 gap-6">
        <div className="hl-card p-6 md:col-span-2 space-y-4" data-testid="designer-form">
          <div className="grid md:grid-cols-2 gap-4">
            {FIELDS.map(([k, label, type]) => (
              <div key={k}>
                <label className="overline block mb-1.5">{label}</label>
                <input
                  type={type}
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`designer-${k}`}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                />
              </div>
            ))}
          </div>
          <button onClick={save} disabled={busy}
            data-testid="designer-save"
            className="hl-btn-accent inline-flex items-center gap-2 text-sm disabled:opacity-40">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save branding"}
          </button>
          <div className="text-xs text-slate-500">Every subsequent report, credential and board pack for this tenant will use these settings.</div>
        </div>

        <div className="hl-card p-6" data-testid="designer-preview">
          <div className="overline mb-3"><Palette className="inline w-3 h-3" /> Live preview</div>
          <div className="rounded overflow-hidden border border-slate-200">
            <div style={{ background: form.primary_color || "#002147", padding: "18px", color: "#fff" }}>
              <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">School Report</div>
              <div className="font-display font-bold text-lg mt-1">{data.school.name}</div>
              <div className="italic text-xs mt-1 opacity-80">{form.motto || "—"}</div>
            </div>
            <div style={{ background: "#ffffff", padding: "16px" }}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded" style={{ background: `linear-gradient(135deg, ${form.primary_color || "#002147"} 0 50%, ${form.accent_color || "#D9531E"} 50%)` }} />
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest">Signature</div>
                  <div className="font-display italic text-lg" style={{ color: form.primary_color || "#002147" }}>{form.signature_name || "Principal"}</div>
                </div>
              </div>
              <div style={{ borderTop: `2px solid ${form.accent_color || "#D9531E"}`, marginTop: "12px", paddingTop: "8px" }}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{form.footer_note || "Confidential — Education360™"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
